import { useCallback, useEffect, useRef, useState } from 'react'

import { demoChatMessages } from '../dev/buyerDemoData'
import { demoUiDelay, isDemoUiEnabled } from '../dev/demoMode'
import { apiRequest } from './client'

export type ChatMessage = {
  id: number
  threadId: number
  senderUserProfileId: number
  senderOrgId: number
  body: string
  createdAt: string
}

// Терпимость к обоим написаниям на проводе: API нормализуется к snake_case,
// поэтому читаем camelCase, а при его отсутствии — snake_case.
type ChatMessageRaw = ChatMessage & {
  thread_id?: number
  sender_user_profile_id?: number
  sender_org_id?: number
  created_at?: string
}

const normalizeChatMessage = (raw: ChatMessageRaw): ChatMessage => ({
  id: raw.id,
  threadId: raw.threadId ?? raw.thread_id ?? 0,
  senderUserProfileId: raw.senderUserProfileId ?? raw.sender_user_profile_id ?? 0,
  senderOrgId: raw.senderOrgId ?? raw.sender_org_id ?? 0,
  body: raw.body,
  createdAt: raw.createdAt ?? raw.created_at ?? new Date(0).toISOString(),
})

type ChatMessagesResponse = {
  items: ChatMessageRaw[]
}

const POLL_INTERVAL_MS = 4000

const partnerThreadPath = (organizationId: number, counterpartyOrganizationId: number) =>
  `/organizations/${organizationId}/partner-threads/${counterpartyOrganizationId}`

export const fetchChatMessages = (
  organizationId: number,
  counterpartyOrganizationId: number,
  afterId?: number,
  limit = 50,
) => {
  if (isDemoUiEnabled) {
    const items = demoChatMessages(counterpartyOrganizationId, organizationId)
    const filtered = afterId != null ? items.filter((message) => message.id > afterId) : items
    return demoUiDelay().then(() => ({ items: filtered.slice(0, limit) }))
  }
  const params = new URLSearchParams({ limit: String(limit) })
  if (afterId != null) {
    params.set('afterId', String(afterId))
  }
  return apiRequest<ChatMessagesResponse>(
    `${partnerThreadPath(organizationId, counterpartyOrganizationId)}/messages?${params}`,
  ).then((response) => ({ items: (response.items ?? []).map(normalizeChatMessage) }))
}

export const sendChatMessage = (
  organizationId: number,
  counterpartyOrganizationId: number,
  text: string,
) => {
  if (isDemoUiEnabled) {
    const created: ChatMessage = {
      id: Date.now(),
      threadId: counterpartyOrganizationId,
      senderUserProfileId: 0,
      senderOrgId: organizationId,
      body: text.trim(),
      createdAt: new Date().toISOString(),
    }
    return demoUiDelay().then(() => created)
  }
  return apiRequest<ChatMessageRaw>(`${partnerThreadPath(organizationId, counterpartyOrganizationId)}/messages`, {
    method: 'POST',
    body: { text },
  }).then(normalizeChatMessage)
}

const mergeMessages = (existing: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] => {
  if (incoming.length === 0) {
    return existing
  }
  const byId = new Map(existing.map((message) => [message.id, message]))
  for (const message of incoming) {
    byId.set(message.id, message)
  }
  return [...byId.values()].sort((a, b) => a.id - b.id)
}

export const useChatPolling = (
  organizationId: number,
  counterpartyOrganizationId: number | null,
  active: boolean,
) => {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const lastIdRef = useRef<number | null>(null)

  const loadMessages = useCallback(async (mode: 'initial' | 'poll') => {
    if (counterpartyOrganizationId == null) {
      return
    }
    if (mode === 'initial') {
      setLoading(true)
      setError(null)
    }
    try {
      const afterId = mode === 'poll' ? (lastIdRef.current ?? undefined) : undefined
      const response = await fetchChatMessages(organizationId, counterpartyOrganizationId, afterId)
      setMessages((current) => {
        const next = mode === 'initial' ? response.items : mergeMessages(current, response.items)
        if (next.length > 0) {
          lastIdRef.current = next[next.length - 1]?.id ?? null
        }
        return next
      })
    } catch (loadError) {
      if (mode === 'initial') {
        setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить сообщения')
      }
    } finally {
      if (mode === 'initial') {
        setLoading(false)
      }
    }
  }, [counterpartyOrganizationId, organizationId])

  useEffect(() => {
    if (counterpartyOrganizationId == null) {
      setMessages([])
      lastIdRef.current = null
      setError(null)
      return
    }

    lastIdRef.current = null
    void loadMessages('initial')
  }, [counterpartyOrganizationId, loadMessages])

  useEffect(() => {
    if (!active || counterpartyOrganizationId == null) {
      return
    }

    const timer = window.setInterval(() => {
      void loadMessages('poll')
    }, POLL_INTERVAL_MS)

    return () => {
      window.clearInterval(timer)
    }
  }, [active, counterpartyOrganizationId, loadMessages])

  const handleSend = async (text: string) => {
    if (counterpartyOrganizationId == null) {
      return false
    }
    const trimmed = text.trim()
    if (!trimmed) {
      return false
    }
    setSending(true)
    setError(null)
    try {
      const created = await sendChatMessage(organizationId, counterpartyOrganizationId, trimmed)
      setMessages((current) => mergeMessages(current, [created]))
      lastIdRef.current = created.id
      return true
    } catch (sendError) {
      setError(sendError instanceof Error ? sendError.message : 'Не удалось отправить сообщение')
      return false
    } finally {
      setSending(false)
    }
  }

  const isOwnMessage = (message: ChatMessage) => message.senderOrgId === organizationId

  return {
    messages,
    loading,
    error,
    sending,
    handleSend,
    isOwnMessage,
  }
}
